import { ReactNode } from 'react'

const PATTERN = /!\[(.*?)\]\(([^)]*?)\)|\[(.+?)\]\(([^)]*?)\)|\*\*(.+?)\*\*|~~(.+?)~~|\*(.+?)\*|_(.+?)_/g

// Quiz text originates from an external CSV (Google Sheets), so URLs are
// untrusted. Only allow safe schemes / relative URLs to block injection of
// schemes like `javascript:` or `data:text/html`.
function sanitizeUrl(url: string, allowDataImage = false): string | undefined {
  const trimmed = url.trim()
  // Relative URLs, anchors, and query strings are safe.
  if (/^(\/|\.\.?\/|#|\?)/.test(trimmed)) return trimmed
  // Reject anything with a scheme that isn't explicitly allowed.
  const schemeMatch = /^([a-z][a-z0-9+.-]*):/i.exec(trimmed)
  if (!schemeMatch) return trimmed // no scheme -> relative path
  const scheme = schemeMatch[1].toLowerCase()
  if (scheme === 'http' || scheme === 'https' || scheme === 'mailto') return trimmed
  if (allowDataImage && /^data:image\//i.test(trimmed)) return trimmed
  return undefined
}

function parseNodes(text: string): ReactNode[] {
  const nodes: ReactNode[] = []
  const re = new RegExp(PATTERN.source, PATTERN.flags)
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index))
    }

    const key = match.index
    if (match[2] !== undefined) {
      // ![alt](src)
      const src = sanitizeUrl(match[2], true)
      if (src !== undefined) {
        // eslint-disable-next-line @next/next/no-img-element
        nodes.push(<img key={key} src={src} alt={match[1]} />)
      } else {
        // Drop unsafe src but keep the alt text as a fallback.
        nodes.push(match[1])
      }
    } else if (match[4] !== undefined) {
      // [text](href)
      const href = sanitizeUrl(match[4])
      if (href !== undefined) {
        nodes.push(
          <a key={key} href={href} target="_blank" rel="noopener noreferrer">
            {parseNodes(match[3])}
          </a>
        )
      } else {
        // Drop unsafe href but keep the (parsed) link text.
        nodes.push(<span key={key}>{parseNodes(match[3])}</span>)
      }
    } else if (match[5] !== undefined) {
      nodes.push(<strong key={key}>{parseNodes(match[5])}</strong>)
    } else if (match[6] !== undefined) {
      nodes.push(<s key={key}>{parseNodes(match[6])}</s>)
    } else if (match[7] !== undefined) {
      nodes.push(<em key={key}>{parseNodes(match[7])}</em>)
    } else {
      nodes.push(<em key={key}>{parseNodes(match[8])}</em>)
    }

    lastIndex = re.lastIndex
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex))
  }

  return nodes
}

export function parseMarkdown(text?: string): ReactNode {
  if (!text) return null
  return <>{parseNodes(text)}</>
}
